import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { IDtoResponse } from '../models/dtos/response.dto';
import { IDinamycDialogResult } from '../models/dynamic-dialog.model';
import { DynamicDialogComponent } from '../modules/dynamic-dialog/dynamic-dialog.component';
import { IDynamicDialogConfig } from '../modules/dynamic-dialog/model';
import { STATUS } from '../types/enums';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private _dialogRef!: MatDialogRef<DynamicDialogComponent>;
  private modalData$ = new BehaviorSubject<IDynamicDialogConfig>({} as IDynamicDialogConfig);

  private _modalLoading$ = new BehaviorSubject<boolean>(false);
  public modalLoading$ = this._modalLoading$.asObservable();

  private _isPrimaryActionClicked$ = new BehaviorSubject<boolean>(false);
  public isPrimaryActionClicked$ = this._isPrimaryActionClicked$.asObservable();

  constructor(private readonly _dialog: MatDialog) {}

  setModalData(data: IDynamicDialogConfig) {
    this.modalData$.next(data);
  }

  getModalData(): Observable<IDynamicDialogConfig> {
    return this.modalData$.asObservable();
  }

  openModal(modalData: IDynamicDialogConfig) {
    if (this.isModalOpen()) {
      this.close();
    }

    this._dialogRef = this._dialog.open(DynamicDialogComponent, {
      width: '710px',
      height: modalData.height || 'auto',
      data: <IDynamicDialogConfig>{
        title: modalData.title,
        subtitle: modalData.subtitle,
        dialogContent: modalData.dialogContent,
        acceptButtonTitle: modalData.acceptButtonTitle,
        declineButtonTitle: modalData.declineButtonTitle,
        componentName: modalData?.componentName,
        modalComponentData: {
          acceptButtonTitleDisabled: modalData.modalComponentData?.acceptButtonTitleDisabled,
          typeModal: modalData.modalComponentData?.typeModal,
        },
      },
    });

    this.setModalData(modalData);

    this._dialogRef.afterClosed().subscribe(() => {
      this.resetModalState();
    });

    return this._dialogRef;
  }

  private resetModalState(): void {
    this.loading(false);
    this.onClickPrimaryAction(false);
    this.setModalData({} as IDynamicDialogConfig);
  }

  isModalOpen(): boolean {
    return this._dialogRef && this._dialogRef.getState() === MatDialogState.OPEN;
  }

  loading(value: boolean) {
    this._modalLoading$.next(value);
  }

  onClickPrimaryAction(value: boolean) {
    this._isPrimaryActionClicked$.next(value);
  }

  close<T extends IDinamycDialogResult>(data?: T) {
    if (this._dialogRef) {
      this._dialogRef.close(data);
    }
  }

  closeWithSuccessMsg(modalName = '') {
    this.close({
      modalName,
      status: STATUS.COMPLETE,
    });
  }

  closeWithErrorMsg(modalName = '') {
    this.close({
      modalName,
      status: STATUS.ERROR,
    });
  }

  closeWithPendingMsg(modalName = '') {
    this.close({
      modalName,
      status: STATUS.PENDING,
    });
  }

  closeModal() {
    this._dialog.closeAll();
    this.resetModalState();
  }
}
